import React from 'react';
import { getImageUrl } from '@/lib/utils/image';

export interface BaseCardData {
  id?: number;
  title?: string;
  description?: string;
  imageUrl?: string;
  theme?: string;
  metadata: {
    cardType?: string;
    artStyle?: string;
    generation?: {
      model?: string;
      provider?: string;
    };
    themeId?: number;
  };
  creator?: {
    id: string | number;
    username: string;
  };
  stats?: {
    likes: number;
    comments: number;
    isLiked: boolean;
  };
}

interface BaseCardProps {
  cardData: BaseCardData;
}

export const BaseCard: React.FC<BaseCardProps> = ({ cardData }) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Image load error for URL:', imageUrl);
    e.currentTarget.onerror = null;
  };
  
  const imageUrl = getImageUrl(cardData.imageUrl);

  return (
    <div className="w-80 min-h-[500px] relative rounded-lg overflow-hidden border-2 border-game-accent bg-card">
      {/* Card Header */}
      <div className="bg-game-accent/10 p-3 border-b border-game-accent/20">
        <h2 className="text-lg font-semibold text-game-title truncate">
          {cardData.title || 'Untitled'}
        </h2>
        <div className="text-xs text-muted-foreground">
          {cardData.metadata.cardType}
        </div>
      </div>

      {/* Card Image */}
      <div className="relative aspect-square overflow-hidden bg-muted">
        <img 
          src={imageUrl} 
          alt={cardData.title || 'Card Image'}
          className="w-full h-full object-contain"
          onError={handleImageError}
          crossOrigin="anonymous"
        />
        <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
          {cardData.metadata.artStyle}
        </div>
      </div>

      {/* Description Box */}
      <div className="p-4 bg-card">
        <div className="text-sm text-foreground/90 min-h-[120px] whitespace-pre-wrap">
          {cardData.description}
        </div>
      </div>

      {/* Card Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-game-accent/5 border-t border-game-accent/20 p-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{cardData.creator?.username || 'Anonymous'}</span>
            <span>•</span>
            <span>{cardData.theme}</span>
          </div>
          <div className="flex items-center gap-3">
            <span>❤️ {cardData.stats?.likes || 0}</span>
            <span>💭 {cardData.stats?.comments || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseCard; 